import {
  ActionCard,
  Assessment,
  AssessmentType,
  Element,
  ElementKind,
  Fragment,
  Question,
  QuestionTypes,
  Section,
  WidgetData,
  WidgetType,
  Response,
} from '@playbooks/core/src/interfaces/ActionCard'

import { GenericAiWidgetData } from '../types'
import getAvailableTags from '../getAvailableTags'
import getActiveTagNames from '../getActiveTagNames'

import setFlowStatus from '../setFlowStatus'
import {
  assignConditionalItems,
  calculateScores,
  enableSeparator,
  makeAssessmentsChecked,
  resetLastActiveSeparator,
} from '../'
import {
  NodeSearchService,
  NodeSettings,
  IActionCardModelObserver,
  BaseNode,
  SectionNode,
  SeparatorSectionNode,
  ContentSectionNode,
  RecommendationNode,
  WidgetNode,
  GenericAiWidgetNode,
  AssessmentNode,
  SingleselectAssessmentNode,
  MultiselectAssessmentNode,
  ResponseNode,
  QuestionNode,
  TextQuestionNode,
  LongTextQuestionNode,
  NumberQuestionNode,
  AmountQuestionNode,
  DropdownQuestionNode,
  EmailQuestionNode,
  CheckboxQuestionNode,
  DateQuestionNode,
  DateTimeQuestionNode,
  FileQuestionNode,
  PercentageQuestionNode,
  ReplacementProcessor,
  PhoneQuestionNode,
  SignatureQuestionNode,
  FormulaQuestionNode,
} from '../actionCard'
import { sectionTypes } from '@playbooks/core/src/constants'

interface INodeMappingSettings {
  actionCardModelObserver?: IActionCardModelObserver
  actionCardModel: ActionCardModel
}

export class ActionCardModel {
  private __actionCardData: ActionCard
  private __observer?: IActionCardModelObserver
  private __rootNodes?: SectionNode[]
  private __nodes?: BaseNode[]
  private __replacementProcessor?: ReplacementProcessor | null

  public get conditionalItems(): number[] | undefined {
    return this.__actionCardData.conditionalItems
  }

  public get replacementProcessor(): ReplacementProcessor {
    if (!this.__replacementProcessor) {
      this.__replacementProcessor = new ReplacementProcessor(this)
    }

    return this.__replacementProcessor
  }

  public get rootNodes(): Array<BaseNode> {
    if (!this.__rootNodes) {
      this.__rootNodes = this.buildNodes()
    }
    return this.__rootNodes
  }

  public get data(): ActionCard {
    return this.__actionCardData
  }

  public get nodes(): Array<BaseNode> {
    if (!this.__nodes) {
      this.__nodes = this.getSearchService().toArray()
    }
    return this.__nodes
  }

  constructor(actionCardData: ActionCard, observer?: IActionCardModelObserver) {
    this.__actionCardData = actionCardData
    this.__observer = this.adjustObserver(observer)
  }

  public getSearchService(): NodeSearchService {
    return new NodeSearchService(this)
  }

  public getAllTags() {
    return getAvailableTags(this.__actionCardData.sections)
  }

  public getActiveTagNames() {
    return getActiveTagNames(this.__actionCardData)
  }

  public adjustActionCardState() {
    makeAssessmentsChecked(this.__actionCardData)
    assignConditionalItems(this)
    enableSeparator(this.__actionCardData)
    calculateScores(this.__actionCardData)
    resetLastActiveSeparator(this.__actionCardData)
    setFlowStatus(this.__actionCardData)
  }

  private buildNodes() {
    const mappingSettings = {
      actionCardModelObserver: this.__observer,
      actionCardModel: this,
    } as INodeMappingSettings
    return (
      this.__actionCardData?.sections
        ?.filter(section => !section.parent)
        .map(section => mapSection(section, undefined, this.__actionCardData, mappingSettings)) ?? []
    )
  }

  private adjustObserver(baseObserver?: IActionCardModelObserver): IActionCardModelObserver {
    const _this = this
    const observer = {
      onModified: function (node: BaseNode) {
        _this.__replacementProcessor = new ReplacementProcessor(_this)
        baseObserver?.onModified?.(node)
      },
    }

    return observer as IActionCardModelObserver
  }
}

function mapSection(
  section: Section,
  parentNode: BaseNode | undefined,
  actionCardData: ActionCard,
  mappingSettings: INodeMappingSettings,
) {
  const nodeSettings = getNodeSettings(section, mappingSettings)
  let node: SectionNode
  switch (section.type) {
    case sectionTypes.SEPARATOR:
      node = new SeparatorSectionNode(section, parentNode, nodeSettings)
      break
    case sectionTypes.CONTENT:
      node = new ContentSectionNode(section, parentNode, nodeSettings)
      break
    default:
      node = new SectionNode(section, parentNode, nodeSettings)
      break
  }

  if (section.elements) {
    node.children.push(...section.elements.map(x => mapElement(x, mappingSettings, node)))
  }
  if (actionCardData.sections?.find(childSection => childSection.parent === section.id)) {
    node.children.push(
      ...actionCardData.sections
        .filter(childSection => childSection.parent === section.id)
        .map(childSection => mapSection(childSection, node, actionCardData, mappingSettings)),
    )
  }
  return node
}

function mapElement(
  element: Element,
  mappingSettings: INodeMappingSettings,
  parentNode: BaseNode | undefined = undefined,
) {
  switch (element.kind) {
    case ElementKind.ASSESSMENT:
      return mapAssessment(element as Assessment, mappingSettings, parentNode)
    case ElementKind.QUESTION:
      return mapQuestion(element as Question, mappingSettings, parentNode)
    case ElementKind.RECOMMENDATION:
      return mapRecommendation(element as Fragment, mappingSettings, parentNode)
    case ElementKind.WIDGET:
      return mapWidget(element as WidgetData, mappingSettings, parentNode)
    default:
      return new BaseNode(element, parentNode, getNodeSettings(element, mappingSettings))
  }
}

function mapAssessment(
  assessment: Assessment,
  mappingSettings: INodeMappingSettings,
  parentNode: BaseNode | undefined = undefined,
) {
  const nodeSettings = getNodeSettings(assessment, mappingSettings)
  let node: AssessmentNode
  switch (assessment.type) {
    case AssessmentType.MULTI:
      node = new MultiselectAssessmentNode(assessment, parentNode, nodeSettings)
      break
    case AssessmentType.SINGLE:
      node = new SingleselectAssessmentNode(assessment, parentNode, nodeSettings)
      break
    default:
      node = new AssessmentNode(assessment, parentNode, nodeSettings)
      break
  }
  node.children = assessment.responses.map(x => mapResponse(x, mappingSettings, node))
  return node
}

function mapQuestion(
  question: Question,
  mappingSettings: INodeMappingSettings,
  parentNode: BaseNode | undefined = undefined,
) {
  switch (question.type) {
    case QuestionTypes.TEXT:
      return new TextQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.LONG_TEXT:
      return new LongTextQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.NUMBER:
      return new NumberQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.AMOUNT:
      return new AmountQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.DROPDOWN:
      return new DropdownQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.EMAIL:
      return new EmailQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.CHECKBOX:
      return new CheckboxQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.DATE:
      return new DateQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.DATE_TIME:
      return new DateTimeQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.FILE:
      return new FileQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.PERCENTAGE:
      return new PercentageQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.PHONE:
      return new PhoneQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.SIGNATURE:
      return new SignatureQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    case QuestionTypes.FORMULA:
      return new FormulaQuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
    default:
      return new QuestionNode(question, parentNode, getNodeSettings(question, mappingSettings))
  }
}

function mapRecommendation(
  fragment: Fragment,
  mappingSettings: INodeMappingSettings,
  parentNode: BaseNode | undefined = undefined,
) {
  const node = new RecommendationNode(fragment, parentNode, getNodeSettings(fragment, mappingSettings))

  if (fragment.widgets && fragment.widgets.length > 0) {
    node.children = fragment.widgets.map(widgetData => mapWidget(widgetData, mappingSettings, node))
  }
  return node
}

function mapWidget(
  widgetData: WidgetData,
  mappingSettings: INodeMappingSettings,
  parentNode: BaseNode | undefined = undefined,
) {
  const nodeSettings = getNodeSettings(widgetData, mappingSettings)

  if (widgetData.type === WidgetType.GenericAiPrompt) {
    return new GenericAiWidgetNode(widgetData as GenericAiWidgetData, parentNode, nodeSettings)
  }
  return new WidgetNode(widgetData, parentNode, nodeSettings)
}

function mapResponse(
  response: Response,
  mappingSettings: INodeMappingSettings,
  parentNode: BaseNode | undefined = undefined,
) {
  return new ResponseNode(response, parentNode, getNodeSettings(response, mappingSettings))
}

function getNodeSettings(nodeData: any, mappingSettings: INodeMappingSettings): NodeSettings {
  const settings = new NodeSettings()
  settings.observer = getNodeObserver(mappingSettings.actionCardModelObserver as IActionCardModelObserver)
  settings.actionCardModel = mappingSettings.actionCardModel
  return settings
}

function getNodeObserver(actionCardModelObserver?: IActionCardModelObserver): any {
  return {
    onValueSet: (node: BaseNode, newValue: any, oldValue: any) => {
      actionCardModelObserver?.onModified?.(node)
    },
  }
}
